import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { MagnifyingGlass, FileXls, FileCsv, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const fields = [
  { value: "part_number", label: "Part Number" },
  { value: "batch_number", label: "Batch Number" },
  { value: "unique_code", label: "QR Code" },
];

export default function Reports() {
  const [field, setField] = useState("part_number");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    const { data } = await api.get("/admin/reports/search", { params: { field, q } });
    setRows(data);
    setSearched(true);
  };

  const download = async (format) => {
    try {
      const t = localStorage.getItem("tvs_token");
      const url = `${API}/admin/reports/export?format=${format}&field=${field}&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `tvs-qr-report.${format === "excel" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Intelligence</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Reports</h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Filter QR records by part, batch, code, or dealer and export to CSV, Excel, or PDF.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="grid md:grid-cols-[200px_1fr_auto] gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">Search by</Label>
            <Select value={field} onValueChange={setField}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="reports-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fields.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Query</Label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Enter search term (or leave empty for all)"
              className="mt-1.5 h-11"
              data-testid="reports-query-input"
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </div>
          <div className="flex items-end">
            <Button onClick={search} className="h-11 bg-[#253c80] hover:bg-[#1a2a5e] text-white" data-testid="reports-search-button">
              <MagnifyingGlass size={18} weight="bold" className="mr-1.5" /> Search
            </Button>
          </div>
        </div>

        {searched && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{rows.length}</span> result{rows.length !== 1 ? "s" : ""} found
            </div>
            <div className="flex gap-2">
              <Button onClick={() => download("csv")} variant="outline" className="h-10" data-testid="export-csv">
                <FileCsv size={16} weight="bold" className="mr-1.5" /> CSV
              </Button>
              <Button onClick={() => download("excel")} variant="outline" className="h-10" data-testid="export-excel">
                <FileXls size={16} weight="bold" className="mr-1.5" /> Excel
              </Button>
              <Button onClick={() => download("pdf")} variant="outline" className="h-10" data-testid="export-pdf">
                <FilePdf size={16} weight="bold" className="mr-1.5" /> PDF
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Part Number</th>
                <th className="text-left px-4 py-3">Part Name</th>
                <th className="text-left px-4 py-3">Batch</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Verified</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    {searched ? "No matching records." : "Run a search to see results."}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.unique_code} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-[12px] text-slate-700">{r.unique_code}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.part_number}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.part_name}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.batch_number}</td>
                  <td className="px-4 py-2.5">
                    {r.status === "verified" ? (
                      <span className="inline-block rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-semibold">verified</span>
                    ) : (
                      <span className="inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-semibold">unused</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {r.verified_at ? new Date(r.verified_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
